import axios from "axios"
import { getToken, baseUrl, config, requestID } from "../common/utils"
import { getAllEmployees } from "./LeaveService"

// Mock data for demonstration purposes
// In a real application, you would use a database like Prisma with a proper schema

interface Document {
  name: string
  url: string
  type: string
  size: number
}

interface TimelineEvent {
  id: string
  date: string
  title: string
  description: string
  type: string
  actor: string
}

interface Penalty {
  type: string
  description: string
  duration: number
  durationType: string
  startDate: string
  endDate: string
}

interface DisciplinaryCase {
  id: string
  caseNumber: string
  employeeId: string
  employeeName: string
  department: string
  issueDate: string
  responseDeadline: string
  status: string
  issueType: string
  issueDescription: string
  responseSubmitted: boolean
  responseDate?: string
  responseText?: string
  documents?: Document[]
  timeline: TimelineEvent[]
  penalty?: Penalty
  isManager: boolean
  penaltyEndDate?: string
  penaltyType?: string
  penaltyDescription?: string
}

// Mock data
const disciplinaryCases: DisciplinaryCase[] = [
  {
    id: "1",
    caseNumber: "DC-2025-001",
    employeeId: "EMP001",
    employeeName: "John Doe",
    department: "Engineering",
    issueDate: "2025-05-01",
    responseDeadline: "2025-05-03",
    status: "query_issued",
    issueType: "attendance",
    issueDescription:
      "Repeated late arrivals (more than 30 minutes) on April 25, 26, and 27, 2025 without prior notification.",
    responseSubmitted: false,
    documents: [
      {
        name: "attendance_log.pdf",
        url: "/documents/attendance_log.pdf",
        type: "application/pdf",
        size: 1024000,
      },
    ],
    timeline: [
      {
        id: "t1",
        date: "2025-05-01T10:30:00Z",
        title: "Disciplinary Query Issued",
        description: "Query issued for repeated late arrivals without notification",
        type: "query_issued",
        actor: "Jane Smith (HR Manager)",
      },
    ],
    isManager: false,
  },
  {
    id: "2",
    caseNumber: "DC-2025-002",
    employeeId: "EMP002",
    employeeName: "Jane Smith",
    department: "Marketing",
    issueDate: "2025-04-15",
    responseDeadline: "2025-04-17",
    status: "response_submitted",
    issueType: "performance",
    issueDescription: "Failure to meet Q1 2025 marketing campaign targets by a significant margin (30% below target).",
    responseSubmitted: true,
    responseDate: "2025-04-16",
    responseText:
      "I acknowledge that the Q1 targets were not met. This was primarily due to unexpected budget cuts in February which were not accounted for when setting the original targets. I have prepared a revised strategy for Q2 that accounts for the current budget constraints while maximizing impact.",
    documents: [],
    timeline: [
      {
        id: "t2-1",
        date: "2025-04-15T14:20:00Z",
        title: "Disciplinary Query Issued",
        description: "Query issued for performance issues related to Q1 targets",
        type: "query_issued",
        actor: "Robert Johnson (Marketing Director)",
      },
      {
        id: "t2-2",
        date: "2025-04-16T11:45:00Z",
        title: "Response Submitted",
        description: "Employee submitted response explaining budget constraints",
        type: "response_submitted",
        actor: "Jane Smith (Marketing Manager)",
      },
    ],
    isManager: false,
  },
  {
    id: "3",
    caseNumber: "DC-2025-003",
    employeeId: "EMP003",
    employeeName: "Robert Johnson",
    department: "Finance",
    issueDate: "2025-03-10",
    responseDeadline: "2025-03-12",
    status: "penalty_applied",
    issueType: "policy_violation",
    issueDescription:
      "Violation of company data security policy by sharing confidential financial information via personal email.",
    responseSubmitted: true,
    responseDate: "2025-03-11",
    responseText:
      "I acknowledge the policy violation. I was trying to work from home due to a family emergency and made a poor decision to send the files to my personal email. I understand the seriousness of this breach and will complete additional security training immediately.",
    documents: [
      {
        name: "email_evidence.pdf",
        url: "/documents/email_evidence.pdf",
        type: "application/pdf",
        size: 2048000,
      },
    ],
    timeline: [
      {
        id: "t3-1",
        date: "2025-03-10T09:15:00Z",
        title: "Disciplinary Query Issued",
        description: "Query issued for data security policy violation",
        type: "query_issued",
        actor: "Emily Davis (Compliance Officer)",
      },
      {
        id: "t3-2",
        date: "2025-03-11T16:30:00Z",
        title: "Response Submitted",
        description: "Employee acknowledged violation and explained circumstances",
        type: "response_submitted",
        actor: "Robert Johnson (Financial Analyst)",
      },
      {
        id: "t3-3",
        date: "2025-03-15T10:00:00Z",
        title: "Penalty Applied",
        description: "Salary deduction of 10% for 2 months and mandatory security training",
        type: "penalty_applied",
        actor: "Michael Wilson (CFO)",
      },
    ],
    penalty: {
      type: "Salary Deduction",
      description: "10% salary deduction and mandatory security training",
      duration: 2,
      durationType: "months",
      startDate: "2025-04-01",
      endDate: "2025-05-31",
    },
    penaltyEndDate: "2025-05-31",
    penaltyType: "Salary Deduction",
    penaltyDescription: "10% salary deduction and mandatory security training",
    isManager: false,
  },
  {
    id: "4",
    caseNumber: "DC-2025-004",
    employeeId: "EMP004",
    employeeName: "Emily Davis",
    department: "HR",
    issueDate: "2025-02-20",
    responseDeadline: "2025-02-22",
    status: "closed",
    issueType: "conduct",
    issueDescription:
      "Unprofessional conduct during team meeting on February 18, 2025, including raising voice and using inappropriate language.",
    responseSubmitted: true,
    responseDate: "2025-02-21",
    responseText:
      "I sincerely apologize for my behavior during the meeting. I was under significant personal stress but that does not excuse my unprofessional conduct. I have already apologized to the team members present and am taking steps to ensure this doesn't happen again, including stress management counseling.",
    documents: [],
    timeline: [
      {
        id: "t4-1",
        date: "2025-02-20T13:45:00Z",
        title: "Disciplinary Query Issued",
        description: "Query issued for unprofessional conduct during team meeting",
        type: "query_issued",
        actor: "Michael Wilson (HR Director)",
      },
      {
        id: "t4-2",
        date: "2025-02-21T10:20:00Z",
        title: "Response Submitted",
        description: "Employee apologized and explained steps taken to address behavior",
        type: "response_submitted",
        actor: "Emily Davis (HR Specialist)",
      },
      {
        id: "t4-3",
        date: "2025-02-25T14:00:00Z",
        title: "Warning Issued",
        description: "Formal warning issued with no further penalties",
        type: "warning_issued",
        actor: "Michael Wilson (HR Director)",
      },
      {
        id: "t4-4",
        date: "2025-03-25T09:30:00Z",
        title: "Case Closed",
        description: "Case closed after review of follow-up conduct",
        type: "case_closed",
        actor: "Michael Wilson (HR Director)",
      },
    ],
    isManager: false,
  },
]

// API configuration with auth headers
const api = axios.create({
  baseURL: "http://example.com/api",
  headers: {
    "Content-Type": "application/json",
  },
})

// Add request interceptor to include the auth token on each request
api.interceptors.request.use(
  (config) => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

export async function getDisciplinaryCases(): Promise<DisciplinaryCase[]> {
  
  // await new Promise((resolve) => setTimeout(resolve, 500))

  // return disciplinaryCases
  try {
    const data = {
      // user_id: userId,
      requestID: requestID(),
    }
    const response = await axios.post(
      `${baseUrl()}disciplinarys/getAllDisciplinary`,
      data,
      config,
    )
    console.log("API Response:", response)

    if (Array.isArray(response.data.data)) {
      
      return response.data.data;
    }

    
  } catch (error) {
    console.error("Error fetching disciplinary cases:", error)
    throw new Error("Failed to fetch disciplinary cases")
  }
}



export async function getDisciplinaryCase(userId: string): Promise<DisciplinaryCase[]> {
  try {
    const data = {
      user_id: userId,
      requestID: requestID(),
    }
    const response = await axios.post(
      `${baseUrl()}disciplinarys/getDisciplinaryByUserId`,
      data,
      config,
    )
    console.log("API Response:", response)

    if (Array.isArray(response.data.data)) {
      
      return response.data.data.map((caseData: any) => ({
        issuedByName: caseData.issuedByName || caseData.managerName,
        id: caseData.id,
        disciplinaryID: caseData.discipline_id || caseData.id,
        caseNumber: caseData.id || `DC-${caseData.id}`,
        employeeId: caseData.user_id || caseData.userId,
        employeeName: caseData.employeeName || caseData.userName,
        department: caseData.department,
        issueDate: caseData.date_created || caseData.created_at,
        responseDeadline: caseData.responseDeadline || caseData.deadline,
        status: caseData.status,
        issueType: caseData.issue_type || caseData.type,
        issueDescription: caseData.issueDescription || caseData.description,
        responseSubmitted: !!caseData.responseText || !!caseData.response,
        responseDate: caseData.responseDate,
        responseText: caseData.responseText || caseData.response,
        documents: caseData.documents || [],
        timeline: caseData.timeline || [],
        penalty: caseData.penalty_id,
        penaltyEndDate: caseData.penaltyEndDate,
        isManager: false,
        description: caseData.description,
        // department: caseData.description,
      }))
    }

    
  } catch (error) {
    console.error("Error fetching disciplinary cases:", error)
    throw new Error("Failed to fetch disciplinary cases")
  }
}


export async function submitDisciplinaryResponse(formData: FormData): Promise<{ success: boolean }> {
  // Validate inputs
  const caseId = formData.get("caseId") as string
  const response = formData.get("response") as string

  if (!response) {
    throw new Error("Missing required fields")
  }

  try {
    // Make the actual API call to the endpoint for employee response
    const apiResponse = await axios.post(`${baseUrl()}disciplinarys/respondDisciplinary`,formData,config)
    console.log(apiResponse);
    console.log("API Response:", apiResponse.data.status)

    // Check if the API call was successful
    if (apiResponse.data.status) {
      // revalidatePath("/disciplinary")
      // revalidatePath(`/disciplinary/${caseId}`)
      return {
        success: true,
      }
    } else {
      throw new Error(apiResponse.data?.message || "Failed to submit disciplinary response")
    }
  } catch (error) {
    console.error("Error submitting disciplinary response:", error)
    throw new Error(error instanceof Error ? error.message : "Failed to submit disciplinary response")
  }
}

export async function takeDisciplinaryAction(formData: FormData): Promise<{ success: boolean }> {
  // In a real application, you would save this to a database
  const disciplinary_id = formData.get("disciplinary_id") as string
  const actionType = formData.get("actionType") as string

  // Validate inputs
  if (!disciplinary_id || !actionType) {
    throw new Error("Missing required fields")
  }

  // Simulate API delay 
  // await new Promise((resolve) => setTimeout(resolve, 1000))

  const apiResponse = await axios.post(`${baseUrl()}disciplinarys/giveDisciplinaryPenalty`,formData,config)
    console.log("API Response:", apiResponse)

  // In a real app, you would update the database record
  // For demonstration, we're just returning success

  return {
    success: apiResponse.data.status || false, // Use the status from the API response
  }
}

export async function issueDisciplinaryQuery(formData: FormData): Promise<{ success: boolean; id: string }> {
  // Validate inputs
  const employeeId = formData.get("user_id") as string
  const issueType = formData.get("issue_type") as string

  if (!employeeId || !issueType) {
    throw new Error("Missing required fields")
  }
  for (const [key, value] of formData.entries()) {
    console.log(`${key}:`, value)
  }

  try {
    // Make the actual API call to the endpoint
    const response = await axios.post(`${baseUrl()}disciplinarys/add`, formData, config)
    console.log("API Response:", response)

    // Check if the API call was successful
    if (response.data) {
      // revalidatePath("/disciplinary")
      return {
        success: true,
        id: response.data.penalty_id || response.data.caseId || "new-case-id", // Use the ID from the response or fallback
      }
    } else {
      throw new Error(response.data?.message || "Failed to issue disciplinary query")
    }
  } catch (error) {
    console.error("Error issuing disciplinary query:", error)
    // throw new Error(error instanceof Error ? error.message : "Failed to issue disciplinary query")
  }
}

